import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Select, Spin } from 'antd';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { formatPhoneNumber } from '../../util/phoneNumber';
import './EmrProviderAutocomplete.scss';

/**
 * EmrProviderAutocomplete - Autocomplete component for selecting EMR providers
 * (internal providers within the EMR system that documents can be assigned to).
 *
 * This is distinct from ReferringProviderAutocomplete which is for external
 * referring providers.
 *
 * With `credentialId`, it searches that credential's NextGen referring-provider
 * directory instead (name prefix or exact 10-digit NPI). Those results carry
 * address, phone and NPI so a user can tell apart one provider's entries at
 * different locations, and inactive providers are tagged.
 *
 * With `onRequestProvider`, a finished search ends with a way to request a
 * provider the directory doesn't have (ENG-3658); it is called with the text
 * the user searched for. With `onMarkAwaitingProvider`, the same footer offers
 * to set the document aside until the provider is added (ENG-3659), e.g. when
 * someone already requested it. With `actionsBlockedReason`, both stay visible
 * but disabled, with the reason beneath them: a document is set aside only once
 * everything but the provider is filled in (ENG-3934).
 */
const NPI_SEARCH = /^\d{10}$/;

// A directory row can come without a name (an NPI search doesn't need one), so
// never fall back to the internal slug.
const providerName = (provider) => provider.name || (provider.npi ? `NPI ${provider.npi}` : 'Unnamed provider');

const providerLabel = (provider) => [providerName(provider), provider.degree].filter(Boolean).join(' ');

const optionLabel = (provider) => `${providerLabel(provider)}${provider.inactive ? ' (inactive)' : ''}`;

const providerDetails = (provider) => [
  provider.address,
  formatPhoneNumber(provider.phone_number),
  provider.npi && `NPI ${provider.npi}`,
].filter(Boolean).join(' · ');

// Exact duplicates are collapsed by the server, so a name that still appears
// more than once belongs to entries that differ (usually the location).
export const duplicateNameHint = (providers) => {
  const counts = providers.filter((provider) => provider.name).reduce((acc, provider) => {
    acc[provider.name] = (acc[provider.name] || 0) + 1;
    return acc;
  }, {});
  const repeated = Object.keys(counts).filter((name) => counts[name] > 1);
  if (repeated.length === 0) return null;
  if (repeated.length === 1) {
    return `More than one provider is named ${repeated[0]}. Check the address before choosing.`;
  }
  return 'Some names appear more than once. Check the address before choosing.';
};

export const ProviderOptionContent = ({ provider, showDetails }) => {
  const details = showDetails ? providerDetails(provider) : '';
  return (
    <div className="emr-provider-autocomplete__option">
      <span>
        {providerLabel(provider)}
        {provider.inactive && <span className="emr-provider-autocomplete__inactive"> (inactive)</span>}
      </span>
      {details && <span className="emr-provider-autocomplete__details">{details}</span>}
    </div>
  );
};

ProviderOptionContent.propTypes = {
  provider: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    degree: PropTypes.string,
    npi: PropTypes.string,
    address: PropTypes.string,
    phone_number: PropTypes.string,
    inactive: PropTypes.bool,
  }).isRequired,
  showDetails: PropTypes.bool.isRequired,
};

const EmrProviderAutocomplete = ({
  value, onChange, placeholder, credentialId, onAnalytics, onRequestProvider, onMarkAwaitingProvider, actionsBlockedReason,
  id, required,
}) => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedValue, setSelectedValue] = useState(value || null);
  const [isWaitingForFetch, setIsWaitingForFetch] = useState(false);
  // The latest search failed: its empty list says nothing about the directory,
  // so it must not read as "not found" or offer a request.
  const [searchFailed, setSearchFailed] = useState(false);
  // Only the latest search may set results: a slow "le" must not replace "lee".
  // Every edit, pick and clear moves this on, so a request already running when
  // the text changes can't land in the 500ms before the next search starts.
  const latestRequestRef = useRef(0);
  const invalidateRunningSearch = () => { latestRequestRef.current += 1; };
  // The kind of search ('name' or 'npi') that produced the listed results, kept
  // with them so a pick is reported against the search it came from, not the
  // text typed since.
  const resultsSearchKindRef = useRef(null);

  // Define the core async fetching logic using useCallback
  const coreFetchLogic = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setData([]);
      setFetching(false);
      setIsWaitingForFetch(false);
      return;
    }
    setFetching(true);
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    try {
      const params = { contains: searchQuery };
      if (credentialId) params.credential_id = credentialId;
      const response = await axios.get('/api/v1/emr_providers', { params });
      if (requestId !== latestRequestRef.current) return;
      const providers = response.data.providers || [];
      resultsSearchKindRef.current = NPI_SEARCH.test(searchQuery.trim()) ? 'npi' : 'name';
      setData(providers);
      setSearchFailed(false);
    } catch (error) {
      console.error('Error fetching EMR providers:', error);
      // The previous search's results no longer match the text; don't leave
      // them pickable (e.g. the credential stopped being eligible: 404).
      if (requestId === latestRequestRef.current) {
        resultsSearchKindRef.current = null;
        setData([]);
        setSearchFailed(true);
      }
    } finally {
      if (requestId === latestRequestRef.current) {
        setFetching(false);
        setIsWaitingForFetch(false);
      }
    }
  }, [setData, setFetching, setIsWaitingForFetch, credentialId]);

  // Create the debounced function using useMemo
  const fetchProviders = useMemo(
    () => debounce(coreFetchLogic, 500),
    [coreFetchLogic],
  );

  // Cleanup effect to cancel debounce on unmount
  useEffect(() => {
    return () => {
      fetchProviders.cancel();
    };
  }, [fetchProviders]);

  // Effect to handle initial value prop
  useEffect(() => {
    setSelectedValue(value || null);
    if (value && !searchText) {
      // UUID slugs won't match via the search endpoint — fetch directly by slug instead
      const isSlug = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
      if (isSlug) {
        const fetchBySlug = async () => {
          setFetching(true);
          try {
            const response = await axios.get(`/api/v1/emr_providers/${value}`);
            const provider = response.data;
            if (provider?.id) {
              setData([{ id: provider.id, name: provider.name }]);
            }
          } catch (error) {
            console.error('Error fetching provider by slug:', error);
          } finally {
            setFetching(false);
          }
        };
        fetchBySlug();
      } else {
        fetchProviders(value);
      }
    }
  }, [value]);

  const handleSearch = (newValue) => {
    invalidateRunningSearch();
    setSearchText(newValue);
    setSearchFailed(false);
    if (newValue && newValue.length >= 2) {
      setIsWaitingForFetch(true);
    } else {
      setIsWaitingForFetch(false);
      setFetching(false);
      setData([]);
      fetchProviders.cancel();
    }
    fetchProviders(newValue);
  };

  // A pick from a credential's directory. Counts and flags only: no provider
  // name, NPI or id leaves the browser.
  const trackDirectorySelection = (slug) => {
    if (!credentialId || !slug || !onAnalytics) return;

    const picked = data.find((provider) => provider.id === slug);
    onAnalytics('nextgen_referring_provider_selected', {
      search_kind: resultsSearchKindRef.current,
      result_count: data.length,
      inactive: Boolean(picked?.inactive),
      repeated_name_hint: Boolean(duplicateNameHint(data)),
    });
  };

  const handleChange = (newValue) => {
    invalidateRunningSearch();
    trackDirectorySelection(newValue);
    setSelectedValue(newValue || null);
    setIsWaitingForFetch(false);
    setFetching(false);
    fetchProviders.cancel();

    // Pass the ID to the parent component, and the picked directory entry for
    // callers that show its name (NextGen task templates).
    onChange(newValue || '', data.find((provider) => provider.id === newValue) || null);
  };

  const handleClear = () => {
    invalidateRunningSearch();
    setSelectedValue(null);
    setSearchText('');
    setData([]);
    setSearchFailed(false);
    setIsWaitingForFetch(false);
    setFetching(false);
    fetchProviders.cancel();
    onChange('');
  };

  const getNotFoundContent = () => {
    if (fetching || isWaitingForFetch) {
      return <Spin size="small" />;
    }

    if (!searchText || searchText.length < 2) {
      return 'Type to search providers';
    }

    if (searchFailed) return 'Couldn\'t search providers. Try again.';

    return 'No providers found';
  };

  const hint = credentialId ? duplicateNameHint(data) : null;
  const searchFinished = searchText.length >= 2 && !fetching && !isWaitingForFetch;
  const showFooter = Boolean(onRequestProvider || onMarkAwaitingProvider) && searchFinished && !searchFailed;

  const renderDropdown = (menu) => (
    <>
      {hint && <div className="emr-provider-autocomplete__hint">{hint}</div>}
      {menu}
      {showFooter && (
        <div className="emr-provider-autocomplete__request">
          {data.length > 0 ? 'Not the right provider?' : 'Not in the directory?'}
          {onRequestProvider && (
            <button
              type="button"
              className="emr-provider-autocomplete__request-link"
              disabled={Boolean(actionsBlockedReason)}
              // Keep the click from blurring the select and closing the menu first.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onRequestProvider(searchText)}
            >
              Request a new provider
            </button>
          )}
          {onRequestProvider && onMarkAwaitingProvider && <span aria-hidden="true">·</span>}
          {onMarkAwaitingProvider && (
            <button
              type="button"
              className="emr-provider-autocomplete__request-link"
              disabled={Boolean(actionsBlockedReason)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onMarkAwaitingProvider()}
            >
              Mark Awaiting Provider
            </button>
          )}
          {actionsBlockedReason && (
            <div className="emr-provider-autocomplete__request-blocked">{actionsBlockedReason}</div>
          )}
        </div>
      )}
    </>
  );

  return (
    <Select
      // A label pointing at `id` names the search; antd forwards both onto its input.
      id={id}
      aria-required={required ? 'true' : undefined}
      showSearch
      value={selectedValue}
      placeholder={placeholder || 'Search providers...'}
      defaultActiveFirstOption={false}
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      onClear={handleClear}
      notFoundContent={getNotFoundContent()}
      allowClear
      optionLabelProp="label"
      dropdownRender={renderDropdown}
      // Mount on the page like the drawer's other selects (z-index from
      // ehr-select-dropdown): inside a drawer card the list and its footer get
      // cut off by the card's overflow.
      getPopupContainer={() => document.body}
      dropdownClassName="ehr-select-dropdown"
    >
      {data.map((provider) => (
        <Select.Option
          key={provider.id}
          value={provider.id}
          label={optionLabel(provider)}
          // rc-select only derives these from plain-text children, which the
          // option no longer has.
          title={optionLabel(provider)}
          aria-label={optionLabel(provider)}
        >
          <ProviderOptionContent provider={provider} showDetails={Boolean(credentialId)} />
        </Select.Option>
      ))}
    </Select>
  );
};

EmrProviderAutocomplete.defaultProps = {
  value: '',
  placeholder: 'Search providers...',
  credentialId: null,
  onAnalytics: null,
  onRequestProvider: null,
  onMarkAwaitingProvider: null,
  actionsBlockedReason: null,
  id: undefined,
  required: false,
};

EmrProviderAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAnalytics: PropTypes.func,
  onRequestProvider: PropTypes.func,
  onMarkAwaitingProvider: PropTypes.func,
  actionsBlockedReason: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
};

export default EmrProviderAutocomplete;
